import { useState, useEffect, useContext } from 'react';
import { find, map, orderBy, values } from 'lodash';
import fetch from 'cross-fetch';
import { saveField } from '../../utils';
import { setLastAction, websiteDisable } from '../data/actions';
import cogoToast from 'cogo-toast';
import { DispatchContext, StateContext } from '../../App';
import { useMutation } from 'graphql-hooks';
import { DeleteDraftMutation, DraftToProductionMutation, ExecuteMutation, PublishMutation, SiteUpdateMutation } from '../data/graphql';
import dayjs from 'dayjs';
import { v4 } from 'uuid';

export const mapPages = (pagesData, isCopy = false) => {
  return map(values(pagesData), (page) => {
    return {
      uuid: !isCopy ? page.id : v4(),
      url: page.url,
      type: page.type,
      slug: JSON.stringify(page.slug),
      tags: page.tags,
      indexable: page.indexable,
      matchPath: page.matchPath,
      meta: map(page.meta, (meta, index) => {
        return {
          uuid: !isCopy ? meta.id : v4(),
          language: meta.language,
          title: meta.title,
          description: meta.description
        };
      }),
      properties: map(page.properties, (prop) => {
        return {
          id: !isCopy ? prop.id : v4(),
          type: prop.type,
          data: JSON.stringify(prop.data),
          language: prop.language
        };
      }),
      blocks: map(page.blocks, (block, index) => {
        return {
          uuid: !isCopy ? block.id : v4(),
          contentType: block.contentType,
          variant: block.componentName,
          order: index,
          content: map(block.content, (content) => {
            return {
              uuid: !isCopy ? content.id : v4(),
              culture: content.culture,
              fields: map(content.fields, (field) => {
                if (!isCopy) {
                  return {
                    name: field.name,
                    value: !isCopy ? saveField(field) : field.value
                  };
                } else {
                  return {
                    name: field.name,
                    type: field.type,
                    value: !isCopy ? saveField(field) : field.value
                  };
                }
              })
            };
          }),
          campaignId: block.campaignId,
          hideForCampaign: block.hideForCampaign
        };
      })
    };
  });
};

export function UseBuilderActions(language) {
  const state = useContext(StateContext);
  const dispatch = useContext(DispatchContext);
  const currentState = state.history[state.history.length - 1];
  const [saveMutation, { loading: saveLoading }] = useMutation(SiteUpdateMutation);
  const [publishMutation, { loading: publishLoading }] = useMutation(PublishMutation);
  const [convertDraftToProductionMutation, { loading: convertDraftToProductionLoading }] = useMutation(DraftToProductionMutation);
  const [deleteDraftMutation, { loading: deleteDraftLoading }] = useMutation(DeleteDraftMutation);
  const [executeMutation, { loading: executeLoading }] = useMutation(ExecuteMutation);

  const updateWebsite = () => {
    const currentWebsite = find(state.websites, (w) => w.id === state.currentWebsite);
    const pagesData = mapPages(currentState.pages);

    const customBlocksData = map(currentState.customBlocks, (block) => {
      return {
        uuid: block.name,
        description: block.description,
        culture: block.culture,
        data: JSON.stringify(block.data)
      };
    });

    const campaignsData = map(currentState.campaigns, (campaign) => {
      return {
        uuid: campaign.id,
        name: campaign.name,
        start: dayjs(campaign.start).format('YYYY-MM-DD'),
        stop: dayjs(campaign.stop).format('YYYY-MM-DD'),
        isDraft: campaign.isDraft,
        disabled: campaign.disabled | false
      };
    });

    const advertisementGroupsData = map(currentState.advertisementGroups, (group) => {
      return {
        id: group.id,
        name: group.name,
        isDraft: group.isDraft,
        disabled: group.disabled
      };
    });

    const advertisementsData = map(currentState.advertisements, (advertisement) => {
      return {
        id: advertisement.id,
        description: advertisement.description,
        data: advertisement.data,
        culture: advertisement.culture,
        reference: advertisement.reference,
        groups: advertisement.groups,
        isDraft: advertisement.isDraft,
        disabled: advertisement.disabled
      };
    });

    const vehicleSearchFiltersData = map(currentState.vehicleSearchFilters, (f) => {
      return {
        id: f.id,
        name: JSON.stringify(f.name),
        type: f.type,
        filters: JSON.stringify(f.filters),
        isDraft: f.isDraft,
        disabled: f.disabled
      };
    });

    const pageContent = map(currentState.pageContentSEO, (content) => {
      return {
        ...content,
        filters: JSON.stringify(content.filters),
        text: JSON.stringify(content.text)
      };
    });

    saveMutation({
      variables: {
        data: {
          website: {
            uuid: currentWebsite.id,
            name: currentWebsite.name,
            pages: pagesData
          },

          pageContent: pageContent,
          customBlocks: customBlocksData,
          campaigns: campaignsData,

          advertisementGroups: advertisementGroupsData,
          advertisements: advertisementsData,
          vehicleSearchFilters: vehicleSearchFiltersData
        }
      }
    })
      .then((res) => {
        const { ok } = res.data.siteUpdate;
        if (ok) {
          setLastAction(dispatch);
          cogoToast.success('Layout saved!');
        }
      })
      .catch((error) => {
        cogoToast.error('Error saving layout!');
      });
  };

  const publishWebsite = () => {
    publishMutation().then((result) => {
      const { publish } = result.data;
      if (publish.ok) {
        setLastAction(dispatch);
        cogoToast.success('Website published!');
      } else {
        cogoToast.error('Error publishing website!');
      }
    });
  };

  const executeScript = (action, project, payload = {}) => {
    executeMutation({
      variables: {
        action: action,
        project: project,
        payload: JSON.stringify(payload)
      }
    }).then((r) => {
      const { execute } = r.data;
      if (execute.ok) {
        setLastAction(dispatch);
        cogoToast.success('Success!');
      } else {
        cogoToast.error('Error!');
      }
    });
  };

  const convertDraftToProduction = (websiteId) => {
    convertDraftToProductionMutation({
      variables: {
        website: websiteId
      }
    }).then((result) => {
      const { draftToProduction } = result.data;
      if (draftToProduction.ok) {
        executeScript('release', 'website', {});
      }
    });
  };

  const draftDelete = (websiteId) => {
    deleteDraftMutation({
      variables: {
        website: websiteId
      }
    }).then((result) => {
      websiteDisable(dispatch, websiteId);
    });
  };

  return {
    dispatch,
    updateWebsite,
    convertDraftToProduction,
    draftDelete,
    publishWebsite,
    executeScript,
    loading: saveLoading || publishLoading || executeLoading
  };
}

export default UseBuilderActions;
